import { toast } from 'sonner'
import { paths } from '@/routes/paths'
import { User } from '@/types/user.type'
import { useState, useEffect } from 'react'
import { fError } from '@/utils/format-error'
import { useBoolean } from '@/hooks/use-boolean'
import { useRouter } from '@/routes/hooks/use-router'
import axiosInstance, { endpoints } from '@/lib/axios'
import CustomPagination from '@/components/pagination/custom-pagination'
import ConfirmDialog from '@/components/share-components/confirm-dialog'
import LoadingSection from '@/components/loading-indicator/loading-section'
import { ProductsPaginationResponse } from '@/sections/products/product.types'

import ShopsTable from '../shops-table'
import ShopsFilter from '../shops-filter'

// Constants
const PAGE_SIZE = 10

export default function ShopsView() {
  const router = useRouter()
  const loading = useBoolean(true)

  // State
  const [shops, setShops] = useState<User[]>([])
  const [pageMeta, setPageMeta] = useState({
    total: 0,
    page: 0,
    limit: 0,
  })

  const [filters, setFilters] = useState({
    page: 1,
    limit: PAGE_SIZE,
    search: '',
    status: '',
    role: 'SHOP', // Default to SHOP only
    orderBy: 'createdAt:desc',
  })

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean
    shop: User | null
  }>({
    open: false,
    shop: null,
  })

  // Loading state for delete operation
  const [deleteLoading, setDeleteLoading] = useState(false)

  // Fetch shops data
  const fetchShops = async () => {
    try {
      loading.onTrue()

      // เตรียม parameters สำหรับ API
      const apiParams = {
        page: filters.page,
        limit: filters.limit,
        orderBy: filters.orderBy,
        ...(filters.search && { search: filters.search }),
        ...(filters.status && { status: filters.status }),
        ...(filters.role && { role: filters.role }),
      }

      const { data } = await axiosInstance.get<
        ProductsPaginationResponse<User>
      >(endpoints.users.list, { params: apiParams })

      setShops(data.items)
      setPageMeta({
        total: data.total,
        page: filters.page,
        limit: filters.limit,
      })
    } catch (error) {
      console.error('Error fetching shops:', error)
      toast.error(fError(error))
      setShops([])
    } finally {
      loading.onFalse()
    }
  }

  // Initial load และ refetch เมื่อ filters เปลี่ยน
  useEffect(() => {
    fetchShops()
  }, [filters])

  const handleAddClick = () => {
    router.push(paths.shopCreate)
  }

  const handleEdit = (shop: User) => {
    router.push(paths.shopEdit(shop.uid))
  }

  const handleDelete = (shop: User) => {
    setConfirmDialog({
      open: true,
      shop,
    })
  }

  const handleConfirmDelete = async () => {
    if (!confirmDialog.shop || deleteLoading) return

    try {
      setDeleteLoading(true)

      await axiosInstance.delete(endpoints.users.delete(confirmDialog.shop.uid))
      toast.success('ลบร้านค้าสำเร็จ')

      // Refresh the list
      fetchShops()
    } catch (error) {
      console.error(error)
      toast.error(fError(error))
    } finally {
      setDeleteLoading(false)
      setConfirmDialog({ open: false, shop: null })
    }
  }

  // Handle pagination
  const handlePageChange = (page: number) => {
    setFilters((prev) => ({ ...prev, page }))
  }

  return (
    <LoadingSection loading={loading.value}>
      <div className="space-y-6">
        {/* Filter Section */}
        <ShopsFilter onAddClick={handleAddClick} />

        {/* Table Section */}
        <ShopsTable
          shops={shops}
          loading={loading.value}
          // onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />

        {/* Pagination */}
        {Math.ceil(pageMeta.total / pageMeta.limit) > 1 && (
          <CustomPagination
            currentPage={filters.page}
            totalPages={Math.ceil(pageMeta.total / pageMeta.limit)}
            onPageChange={handlePageChange}
          />
        )}
      </div>

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        onOpenChange={(open) => setConfirmDialog({ open, shop: null })}
        title="ยืนยันการลบ"
        description={`คุณต้องการลบร้านค้า "${confirmDialog.shop?.displayName}" หรือไม่? การดำเนินการนี้ไม่สามารถยกเลิกได้`}
        confirmText="ลบ"
        cancelText="ยกเลิก"
        variant="destructive"
        onConfirm={handleConfirmDelete}
        loading={deleteLoading}
      />
    </LoadingSection>
  )
}
